import {
  type ReplaceSupplierMeasurementCapabilitiesRequest,
  type SupplierMeasurementCapability,
  type SupplierMeasurementEquipment
} from '@tandemone/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import { useSetupState } from '@/domains/setup'

import { getSupplierMeasurement, replaceSupplierMeasurement } from './api'
import {
  QA_SKIP_CERTS_LABEL,
  SUPPLIER_MEASUREMENT_EQUIPMENT_KINDS,
  SUPPLIER_MEASUREMENT_EQUIPMENT_OPTIONS
} from './enum-labels'
import { FormSkeleton } from './form-skeleton'
import { invalidateOnboarding } from './invalidate-onboarding'
import { PrimaryButton } from './primary-button'
import { StepFooter } from './step-footer'
import { TogglePill } from './toggle-pill'

type RowState = {
  inHouse: boolean
  subContracted: boolean
}

type FormState = Record<SupplierMeasurementEquipment, RowState>

function emptyRow(): RowState {
  return { inHouse: false, subContracted: false }
}

function buildInitial(
  rows: ReadonlyArray<SupplierMeasurementCapability>
): FormState {
  const state = {} as FormState
  for (const kind of SUPPLIER_MEASUREMENT_EQUIPMENT_KINDS) {
    state[kind] = emptyRow()
  }
  for (const row of rows) {
    state[row.equipmentType] = {
      inHouse: row.inHouse,
      subContracted: row.subContracted
    }
  }
  return state
}

export function SupplierMeasurementForm({
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  onSuccess: () => void
  submitLabel?: string
}) {
  const queryClient = useQueryClient()
  const { setupState } = useSetupState()
  const [state, setState] = useState<FormState | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-measurement'],
    queryFn: ({ signal }) => getSupplierMeasurement(signal)
  })

  const skippedByCert =
    setupState?.supplier?.qualityAssessmentSkippedByCert ?? false

  const mutation = useMutation({
    mutationFn: (body: ReplaceSupplierMeasurementCapabilitiesRequest) =>
      replaceSupplierMeasurement(body),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'supplier-measurement')
      onSuccess()
    },
    onError: (err: unknown) => setSubmitError(extractErrorMessage(err))
  })

  if (isLoading) return <FormSkeleton />

  const current = state ?? buildInitial(data?.rows ?? [])
  const totalKinds = SUPPLIER_MEASUREMENT_EQUIPMENT_KINDS.length
  const completeCount = SUPPLIER_MEASUREMENT_EQUIPMENT_KINDS.filter(
    kind => current[kind].inHouse || current[kind].subContracted
  ).length
  const canSubmit = completeCount === totalKinds

  function toggle(
    kind: SupplierMeasurementEquipment,
    which: 'inHouse' | 'subContracted'
  ) {
    setState(prev => {
      const base = prev ?? current
      const row = base[kind]
      return { ...base, [kind]: { ...row, [which]: !row[which] } }
    })
    setSubmitError(null)
  }

  function handleSubmit() {
    if (!canSubmit || mutation.isPending) return
    mutation.mutate({
      rows: SUPPLIER_MEASUREMENT_EQUIPMENT_KINDS.map(kind => ({
        equipmentType: kind,
        inHouse: current[kind].inHouse,
        subContracted: current[kind].subContracted
      }))
    })
  }

  return (
    <div className='flex w-full flex-col gap-4'>
      {skippedByCert && (
        <div className='flex items-start justify-between gap-4 rounded-2xl border border-accent bg-accent-light px-6 py-4'>
          <div>
            <p className='text-[14px] font-medium text-ink'>
              Auto-completed by your certification
            </p>
            <p className='mt-1 text-[13px] text-ink-soft'>
              You hold {QA_SKIP_CERTS_LABEL}, so this step is not required. You
              can still fill it in if you want to share more detail with buyers.
            </p>
          </div>
          <PrimaryButton type='button' onClick={onSuccess}>
            Done
          </PrimaryButton>
        </div>
      )}

      <section className='rounded-2xl border border-border bg-page p-4 sm:p-6'>
        <ul className='grid gap-2 sm:grid-cols-2'>
          {SUPPLIER_MEASUREMENT_EQUIPMENT_KINDS.map(kind => {
            const row = current[kind]
            const option = SUPPLIER_MEASUREMENT_EQUIPMENT_OPTIONS[kind]
            const missing = !row.inHouse && !row.subContracted
            return (
              <li
                key={kind}
                className={`flex flex-col gap-2 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:gap-3 ${
                  missing
                    ? 'border-amber-400 bg-amber-50/30 dark:border-amber-600 dark:bg-amber-950/30'
                    : 'border-accent bg-accent-light/30'
                }`}
              >
                <p className='min-w-0 flex-1 text-[14px] font-medium text-ink'>
                  {option.label}
                </p>
                <div
                  role='group'
                  aria-label={`${option.label} sourcing`}
                  aria-invalid={missing || undefined}
                  className='flex shrink-0 items-center gap-2'
                >
                  <TogglePill
                    label='In-house'
                    selected={row.inHouse}
                    missing={missing}
                    onClick={() => toggle(kind, 'inHouse')}
                  />
                  <TogglePill
                    label='Sub-contracted'
                    selected={row.subContracted}
                    missing={missing}
                    onClick={() => toggle(kind, 'subContracted')}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      </section>

      {submitError && <p className='text-sm text-red-600'>{submitError}</p>}

      <StepFooter hint={`${completeCount} of ${totalKinds} answered`}>
        <PrimaryButton
          type='button'
          disabled={!canSubmit || mutation.isPending}
          onClick={handleSubmit}
        >
          {mutation.isPending ? 'Saving…' : submitLabel}
        </PrimaryButton>
      </StepFooter>
    </div>
  )
}
